import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import VisibilityOutlinedIcons from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import PaymentProcessor from '@/shared/types/order/paymentProcessor.type';
import SupportedPaymentProcessor from '@/shared/types/order/supportedPaymentProcessor.type';

import usePaymentProcessorsTable_v2 from './hooks/usePaymentProcessorsTable_v2';

type PaymentProcessorsTable_v2Props = {
  supportedPaymentProcessors: SupportedPaymentProcessor[] | null;
  refreshTableIndex: number;
  openViewApiKeyDialog: (apiKey: string) => void;
  openAddPaymentProcessorDialog: () => void;
  openEditPaymentProcessorDialog: (processor: PaymentProcessor) => void;
};

const PaymentProcessorsTable_v2: FC<PaymentProcessorsTable_v2Props> = ({
  supportedPaymentProcessors,
  refreshTableIndex,
  openViewApiKeyDialog,
  openAddPaymentProcessorDialog,
  openEditPaymentProcessorDialog,
}) => {
  const { t } = useTranslation(['paymentProcessors', 'common']);

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    usePaymentProcessorsTable_v2({ refreshTableIndex });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'processor_key':
        return {
          ...column,
          filterable: false,
          renderCell: (params) => {
            const row = params.row as PaymentProcessor;

            if (supportedPaymentProcessors?.length) {
              const supportedProcessor = supportedPaymentProcessors.find(
                (processor) => processor.processor_key === row.processor_key,
              );

              if (supportedProcessor) {
                return supportedProcessor.processor_name;
              }
            }

            return row.processor_key;
          },
        };
      case 'api_key':
        return {
          ...column,
          renderCell: (params) => {
            const row = params.row as PaymentProcessor;

            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography noWrap>{row.api_key.substring(0, 10)}</Typography>

                <IconButton
                  size='small'
                  onClick={() => openViewApiKeyDialog(row.api_key)}
                >
                  <VisibilityOutlinedIcons
                    style={{
                      cursor: 'pointer',
                      color: 'text.secondary',
                      width: 18,
                      height: 18,
                    }}
                  />
                </IconButton>
              </Box>
            );
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '80vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.processor_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        checkboxSelection={false}
        customProps={{
          tableInstance: TableInstance.PaymentProcessors,
          apiRef,
          model,
          toolbar: {
            title: t('paymentProcessors:paymentProcessors'),
            toolbarActions: (
              <>
                <Button
                  variant='contained'
                  size='large'
                  onClick={openAddPaymentProcessorDialog}
                >
                  {t('paymentProcessors:addProcessor')}
                </Button>
              </>
            ),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('paymentProcessors:updateProcessor'),
                  onClick: (apiRef, rowId) => {
                    const currentProcessor = apiRef.current.getRow(
                      rowId || '',
                    ) as PaymentProcessor;
                    openEditPaymentProcessorDialog(currentProcessor);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default PaymentProcessorsTable_v2;
